import { useEffect, useState } from 'react';
import { getTheme, otherTheme, setTheme, subscribeTheme, type Theme } from './theme.ts';

/**
 * Light or dark, on the public page.
 *
 * A single button rather than the console's pair of them. The sidebar's
 * Appearance control is a setting among other settings, where showing both
 * options and which one is on is the right shape; this is one control in a page
 * header, where two buttons would be two-thirds of the chrome on a page whose
 * chrome should be almost nothing.
 *
 * **It is labelled with what it will do, not with what is on.** "Switch to dark
 * mode" is unambiguous read on its own by a screen reader; a toggle labelled
 * "Dark mode" leaves the listener to work out whether that is a description or
 * an instruction. The icon shows the destination for the same reason.
 *
 * Deliberately not `aria-pressed`. This is a switch between two named states,
 * not something that is on or off, and a pressed toggle would announce "Switch
 * to dark mode, pressed" — which reads as the opposite of what happened.
 */

const SunIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="3.6" stroke="currentColor" strokeWidth="1.5" />
    <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.3 4.3l1.4 1.4M14.3 14.3l1.4 1.4M15.7 4.3l-1.4 1.4M5.7 14.3l-1.4 1.4" />
    </g>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M16 12.2A6.6 6.6 0 0 1 7.8 4a6.6 6.6 0 1 0 8.2 8.2Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const LABEL: Record<Theme, string> = {
  light: 'Switch to light mode',
  dark: 'Switch to dark mode',
};

export const ThemeToggle = () => {
  const [theme, setThemeState] = useState<Theme>(getTheme);

  useEffect(() => subscribeTheme(setThemeState), []);

  const next = otherTheme(theme);

  return (
    <button
      type="button"
      className="btn btn-quiet btn-sm theme-toggle"
      title={LABEL[next]}
      aria-label={LABEL[next]}
      onClick={() => setTheme(next)}
    >
      {next === 'dark' ? <MoonIcon /> : <SunIcon />}
      <span className="theme-toggle-text">{next === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  );
};
